// TanStack Router root route.
// -----------------------------------------------------------------
// Per docs/architecture/m3-spa-scaffold-plan.md § 2.5 + § 2.7 +
// dispatch m3-2-auth-rules-listener.md § 6.11.
//
// The root route owns the application-wide providers:
//   - <MantineProvider> — moved out of main.tsx so the router and
//     all routes (including <Notifications />) live inside it.
//   - <Notifications />  — Mantine's notification portal.
//   - <AuthProvider>     — Firebase Auth state context.
//   - <QueryClientProvider> — TanStack Query for mutations
//     (M3.3+; reads stay on raw onSnapshot listeners per audit
//     § 8.3.3 + dispatch m3-3-shared-lib.md § 3.13).
//
// Children render via <Outlet />; TanStack Router walks the
// `_authenticated` / `login` / `no-access` route tree generated
// from `src/routes/`.

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Outlet, createRootRoute } from '@tanstack/react-router';

import { AuthProvider } from '@/auth/AuthContext';

// Single client instance for the SPA. Default options are fine for
// M3.3 (mutations don't touch the cache); M4/M5 may tune retry +
// staleTime per route as listener-vs-fetch boundaries firm up.
const queryClient = new QueryClient();

const RootComponent = () => (
  <MantineProvider defaultColorScheme='light'>
    <Notifications position='top-right' />
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </QueryClientProvider>
  </MantineProvider>
);

export const Route = createRootRoute({
  component: RootComponent,
});
